<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { defaultSwipeConfig, resolveSwipeState } from '$lib/domain/blueprint';
  import { activePlan, nextScheduledPlan, plansStore } from '$lib/stores/plans';
  import { buildPlanSessions, getSessionLabel } from '$lib/domain/scheduler';
  import type { Plan } from '$lib/domain/types';

  const dispatch = createEventDispatcher<{
    openPlan: undefined;
    editPlan: { plan: Plan };
  }>();

  let draggingPlanId: string | null = null;
  let planOverId: string | null = null;
  let openSwipePlanId: string | null = null;
  let swipePlanId: string | null = null;
  let swipeStartX = 0;
  let swipeStartY = 0;

  $: visiblePlan = $activePlan ?? $nextScheduledPlan;
  $: scheduledPlans = $plansStore.plans.filter((plan) => plan.status === 'scheduled');
  $: upcomingPlans = scheduledPlans.slice(0, 3);
  $: startBlockedReason = $activePlan
    ? 'Queued plans wait for the active plan to finish. Pausing keeps its place.'
    : $plansStore.timerBusy
      ? 'Finish or reset the current timer before starting a plan.'
      : '';
  $: scheduledTime = visiblePlan
    ? new Date(visiblePlan.startAt).toLocaleString(undefined, {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';
  $: allSessions = $activePlan
    ? $plansStore.sessions
    : visiblePlan
      ? buildPlanSessions(visiblePlan.id, visiblePlan.configSnapshot)
      : [];
  $: sessions = allSessions.slice(0, 6);
  $: completed = allSessions.filter((session) => session.completed).length;
  $: currentSession = $activePlan ? $plansStore.sessions[$plansStore.activeSessionIndex] : null;

  const startPlan = async (plan: Plan) => {
    openSwipePlanId = null;
    await plansStore.startPlan(plan.id);
  };

  const editPlan = (plan: Plan) => {
    dispatch('editPlan', { plan });
  };

  const skipPlan = async (plan: Plan) => {
    openSwipePlanId = null;
    await plansStore.skipPlan(plan.id);
  };

  const beginPlanDrag = (event: DragEvent, plan: Plan) => {
    draggingPlanId = plan.id;
    planOverId = plan.id;
    event.dataTransfer?.setData('text/plain', plan.id);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  };

  const finishPlanDrop = async (plan: Plan) => {
    if (draggingPlanId && draggingPlanId !== plan.id) {
      await plansStore.moveScheduledPlan(draggingPlanId, plan.id);
    }
    draggingPlanId = null;
    planOverId = null;
  };

  const resetPlanDrag = () => {
    draggingPlanId = null;
    planOverId = null;
  };

  const isInteractiveSwipeTarget = (target: EventTarget | null) =>
    target instanceof HTMLElement &&
    Boolean(target.closest('button, input, label, a, textarea, select, .drag-handle'));

  const beginPlanSwipe = (event: PointerEvent, plan: Plan) => {
    if (isInteractiveSwipeTarget(event.target)) return;
    swipePlanId = plan.id;
    swipeStartX = event.clientX;
    swipeStartY = event.clientY;
  };

  const finishPlanSwipe = (event: PointerEvent, plan: Plan) => {
    if (swipePlanId !== plan.id) return;
    const deltaX = event.clientX - swipeStartX;
    const deltaY = event.clientY - swipeStartY;
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      openSwipePlanId = resolveSwipeState(deltaX, defaultSwipeConfig) ? plan.id : null;
    }
    swipePlanId = null;
  };

  const startQueuedPlan = async (plan: Plan) => {
    openSwipePlanId = null;
    await startPlan(plan);
  };

  const moveQueuedPlan = (plan: Plan) => {
    openSwipePlanId = null;
    editPlan(plan);
  };
</script>

<div
  id="plan-tabpanel"
  class="glass-panel plan-panel"
  role="tabpanel"
  aria-labelledby="plan-panel-title"
  data-testid="plan-panel"
>
  <div class="panel-header">
    <div>
      <p class="kicker">Plan</p>
      <h2 id="plan-panel-title">{visiblePlan ? visiblePlan.title : 'Build the next block'}</h2>
    </div>
  </div>

  <div class="plan-hero">
    <span class="status-dot"></span>
    <div>
      <strong
        >{visiblePlan
          ? visiblePlan.status === 'active'
            ? 'Active plan'
            : 'Next plan'
          : 'No scheduled plan'}</strong
      >
      <span>{visiblePlan ? scheduledTime : 'Choose when to focus and when to take a break.'}</span>
      {#if visiblePlan?.status === 'scheduled' && startBlockedReason}
        <span role="status">{startBlockedReason}</span>
      {/if}
      {#if currentSession}
        <span
          >Step {$plansStore.activeSessionIndex + 1} of {$plansStore.sessions.length}: {getSessionLabel(
            currentSession.type,
          )} · {currentSession.duration} min</span
        >
      {/if}
    </div>
    {#if visiblePlan?.status === 'scheduled'}
      <div class="plan-actions">
        <button
          class="button secondary small"
          type="button"
          disabled={Boolean(startBlockedReason)}
          title={startBlockedReason || undefined}
          aria-label={`Start ${visiblePlan.title}`}
          on:click={() => startPlan(visiblePlan)}
        >
          Start
        </button>
        <button
          class="button ghost small"
          type="button"
          aria-label={`Edit ${visiblePlan.title}`}
          on:click={() => editPlan(visiblePlan)}
        >
          Edit
        </button>
        <button
          class="button ghost small"
          type="button"
          aria-label={`Skip ${visiblePlan.title}`}
          on:click={() => skipPlan(visiblePlan)}
        >
          Skip
        </button>
      </div>
    {/if}
  </div>

  {#if sessions.length}
    <div class="session-cycles compact" role="list" aria-label={`${completed} completed sessions`}>
      {#each sessions as session, index}
        <div
          class="session-cycle"
          class:active={index === $plansStore.activeSessionIndex && Boolean($activePlan)}
          class:complete={session.completed}
          role="listitem"
        >
          <span>{index + 1}</span>
          <strong>{session.duration}:00</strong>
          <small>{getSessionLabel(session.type)}</small>
        </div>
      {/each}
    </div>
  {/if}

  {#if upcomingPlans.length > 1 || ($activePlan && upcomingPlans.length)}
    <div class="plan-queue" aria-label="Upcoming plans">
      <div class="rail-section__header">
        <div>
          <div class="kicker">Queue</div>
          <strong>{scheduledPlans.length} scheduled</strong>
          {#if startBlockedReason}<span role="status">{startBlockedReason}</span>{/if}
        </div>
      </div>
      <div class="list plan-timeline reorder-list" role="list">
        {#each upcomingPlans as plan}
          <div
            class="swipe-row"
            role="listitem"
            aria-label={`Plan actions for ${plan.title}`}
            data-open={openSwipePlanId === plan.id}
            on:pointerdown={(event) => beginPlanSwipe(event, plan)}
            on:pointerup={(event) => finishPlanSwipe(event, plan)}
            on:pointercancel={() => (swipePlanId = null)}
          >
            <div class="swipe-actions" aria-hidden={openSwipePlanId !== plan.id}>
              <button
                class="swipe-action complete"
                type="button"
                disabled={Boolean(startBlockedReason)}
                title={startBlockedReason || undefined}
                tabindex={openSwipePlanId === plan.id ? 0 : -1}
                on:click={() => startQueuedPlan(plan)}
              >
                Start
              </button>
              <button
                class="swipe-action defer"
                type="button"
                tabindex={openSwipePlanId === plan.id ? 0 : -1}
                on:click={() => moveQueuedPlan(plan)}
              >
                Move
              </button>
              <button
                class="swipe-action delete"
                type="button"
                tabindex={openSwipePlanId === plan.id ? 0 : -1}
                on:click={() => skipPlan(plan)}
              >
                Skip
              </button>
            </div>
            <div
              class="plan-row reorder-item swipe-content"
              role="group"
              data-dragging={draggingPlanId === plan.id}
              data-over={planOverId === plan.id && draggingPlanId !== plan.id}
              on:dragover|preventDefault={() => (planOverId = plan.id)}
              on:drop|preventDefault={() => finishPlanDrop(plan)}
              on:dragend={resetPlanDrag}
            >
              <button
                class="drag-handle"
                type="button"
                draggable="true"
                aria-label={`Reorder ${plan.title}`}
                on:dragstart={(event) => beginPlanDrag(event, plan)}
                on:dragend={resetPlanDrag}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8 7h.01M16 7h.01M8 12h.01M16 12h.01M8 17h.01M16 17h.01" />
                </svg>
              </button>
              <div>
                <strong><bdi>{plan.title}</bdi></strong>
                <span
                  >{new Date(plan.startAt).toLocaleString(undefined, {
                    weekday: 'short',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}</span
                >
              </div>
              <div class="plan-actions compact">
                <button
                  class="button secondary small"
                  type="button"
                  disabled={Boolean(startBlockedReason)}
                  title={startBlockedReason || undefined}
                  aria-label={`Start ${plan.title}`}
                  on:click={() => startPlan(plan)}>Start</button
                >
                <button
                  class="button ghost small"
                  type="button"
                  aria-label={`Edit ${plan.title}`}
                  on:click={() => editPlan(plan)}>Edit</button
                >
                <button
                  class="button ghost small"
                  type="button"
                  aria-label={`Skip ${plan.title}`}
                  on:click={() => skipPlan(plan)}>Skip</button
                >
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>
  {/if}

  <button class="button plan-panel__cta" on:click={() => dispatch('openPlan')}>
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
    Schedule Plan
  </button>
</div>
